/**
 * Created by rathompson on 10/25/21
 * File: ClientCredentials
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Bold,
  Title,
  Button,
  ModalFooter,
  ModalBody,
  Input,
  LoadingIcon,
  noop,
  useModal,
  ModalCloseButton
} from '@panwds/react-ui'
import {
  downloadFile
} from '@panwds/react-table'
import { DownloadIcon, RedoIcon } from '@panwds/icons'
import PasswordViewButton from '../../../../../../common/components/form/PasswordViewButton'
import ResetModal from './ResetModal'
import {
  LABEL_CLIENT_CREDENTIALS,
  LABEL_CLIENT_ID,
  LABEL_CLIENT_SECRET,
  BUTTON_DOWNLOAD_CSV,
  BUTTON_RESET_CLIENT_SECRET,
  BUTTON_BACK,
  BUTTON_NEXT,
  PLACEHOLDER_CLIENT_SECRET,
  PLACEHOLDER_CLIENT_ID,
  LABEL_CLIENT_SECRET_MESSAGE,
  LABEL_CLIENT_SECRET_RESET_MESSAGE,
  BUTTON_REMOVE, TOOLTIP_READ_ONLY,
} from '../../../../../constants/LabelConstants'
import {
  DATA_TEST_CLIENT_SECRET,
  DATA_TEST_CLIENT_ID,
  DATA_TEST_CSV
} from '../../../../../../common/constants/TestingDataIds'
import IAMService from '../../../../../services/IAMService'
import { getVarValue } from '../../../../../../common/utils/Sparky'
import { FF_ROLES_ENABLED } from '../../../../../../common/utils/FeatureFlags'
import useReadOnly from '../../../../../../common/hooks/useReadOnly'
import { IDENTITIES_PERMISSION_SET } from '../../../../../constants/IAMConstants'
import TooltipButton from '../../../../../../common/components/tooltipButton/TooltipButton'

const rolesEnabled = getVarValue(FF_ROLES_ENABLED, false)

/**
 *   Form for adding client credentials
 */
const ClientCredentials = ({ tenant, onBackPressed, onNextPressed, account, setAccount, loading: _loading, error: _error, isEdit, onRemove, length }) => {
  /**
   *     ========= Local State ===========
   */
  const { serviceAccounts } = IAMService

  const [passwordVisible, setPasswordVisible] = useState(false)
  const [loading, setLoading] = useState(_loading)
  const [error, setError] = useState('')
  const [hideSecret, setHideSecret] = useState(!account?.client_secret)
  const resetModal = useModal(ResetModal)
  const readOnly = useReadOnly(tenant?.id, IDENTITIES_PERMISSION_SET)

  /**
   *     ========= Lifecycle =============
   */
  useEffect(() => {
    setLoading(_loading)
  }, [_loading])

  useEffect(() => {
    if (_error && _error !== error) {
      setError(_error)
    }
  }, [_error, error])

  /**
   *     ========= Callback ===========
   */
  const onCSVClick = useCallback(() => {
    const file = new Blob([`client_id,client_secret\n${account?.client_id},${account?.client_secret}`], {
      type: 'text/csv;charset=utf-8;',
    })
    const fileName = account?.name || account?.id
    downloadFile({ fileName, file })
  }, [account?.client_id, account?.client_secret, account?.id, account?.name])

  const onPasswordClick = useCallback(() => {
    setPasswordVisible(!passwordVisible)
  }, [passwordVisible])

  const resetSecret = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await serviceAccounts.reset(tenant?.id, account?.id)
      setAccount({ ...account, ...response.data })
      setHideSecret(false)
    }
    catch (e) {
      setError(e.message)
    }
    setLoading(false)
  }, [account, serviceAccounts, setAccount, tenant?.id])

  const onResetClick = useCallback(() => {
    resetModal.openModal({ onDelete: resetSecret })
  }, [resetModal, resetSecret])

  const disabled = useMemo(() => {
    if (rolesEnabled) {
      return loading || !account?.inherited_from?.includes(tenant?.id) || length <= 1
    }
    else {
      return loading || account?.inherited_from !== tenant?.id
    }
  }, [account?.inherited_from, loading, tenant?.id, length])

  return (
    <div style={{ height: '100%', width: '100%' }}>
      <ModalBody addClassName='tw-overflow-y-scroll' style={{ height: 'calc(100% - 43px)', paddingRight: '146px' }}>
        <Title level={1} size={'xs'} addClassName='tw-flex tw-truncate'>{LABEL_CLIENT_CREDENTIALS}</Title>
        {error && <Bold appearance='semibold' addClassName='tw-text-error'>{error}</Bold>}
        <div>
          {loading ? (
            <div className='tw-flex tw-absolute tw-inset-0 tw-items-center'>
              <LoadingIcon size="5xl" addClassName='tw-flex-1'/>
            </div>
          ) : (
            <div>
              {/* Client Id */}
              <div className="tw-mt-2">
                <Bold appearance="semibold">{LABEL_CLIENT_ID}</Bold>
                <Input
                  data-testid={DATA_TEST_CLIENT_ID}
                  label={LABEL_CLIENT_ID}
                  showLabel={false}
                  allowCopy
                  readOnly
                  optional
                  addClassName="tw-mt-1 tw-bg-white"
                  name="client_id"
                  type="text"
                  placeholder={PLACEHOLDER_CLIENT_ID}
                  value={account?.client_id}
                />
              </div>
              {/* Client Secret */}
              <div className="tw-mt-2">
                <Bold appearance="semibold">{LABEL_CLIENT_SECRET}</Bold>
                {hideSecret ? (
                  <>
                    <div className="tw-mt-2">
                      <span
                        className="tw-p-3 tw-border tw-border-solid tw-border-gray-300 tw-rounded-lg tw-bg-blue-100 tw-w-full tw-inline-block dark:tw-bg-gray-1000"
                      >
                        {LABEL_CLIENT_SECRET_RESET_MESSAGE}
                      </span>
                    </div>
                    <div className="tw-mt-2">
                      <Button
                        appearance="secondary"
                        size="lg"
                        icon={<RedoIcon />}
                        onClick={onResetClick}
                        disabled={disabled}
                      >
                        {BUTTON_RESET_CLIENT_SECRET}
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="tw-flex tw-mt-1 tw-gap-2 tw-items-center">
                      <Input
                        data-testid={DATA_TEST_CLIENT_SECRET}
                        label={LABEL_CLIENT_SECRET}
                        showLabel={false}
                        allowCopy
                        readOnly
                        optional
                        addClassName="tw-flex-1 tw-bg-white"
                        name="client_secret"
                        type={passwordVisible ? 'text' : 'password'}
                        placeholder={PLACEHOLDER_CLIENT_SECRET}
                        value={account?.client_secret}
                        suffix={<PasswordViewButton
                          defaultState={passwordVisible}
                          addClassName="tw-p-0.5"
                          style={{ marginRight: '-0.125rem' }}
                          onClick={onPasswordClick}
                        />}
                      />
                      <Button
                        data-testid={DATA_TEST_CSV}
                        appearance="secondary"
                        size="lg"
                        icon={<DownloadIcon />}
                        onClick={onCSVClick}
                      >
                        {BUTTON_DOWNLOAD_CSV}
                      </Button>
                    </div>
                    <div className="tw-mt-2">
                      <span
                        className="tw-p-3 tw-border tw-border-solid tw-border-gray-300 tw-rounded-lg tw-bg-blue-100 tw-w-full tw-inline-block dark:tw-bg-gray-1000"
                      >
                        {LABEL_CLIENT_SECRET_MESSAGE}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
          <ModalFooter style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '43px' }}>
            {isEdit && readOnly ? (
              <TooltipButton
                label={ TOOLTIP_READ_ONLY }
                disabled
                tooltipProps={{ defaultPlacement: 'bottom-end' }}
                buttonProps = {{ appearance: 'secondary-destructive' }}>
                {BUTTON_REMOVE}
              </TooltipButton>
            ) : (
              <ModalCloseButton
                appearance='secondary-destructive'
                onClick={onRemove}
                disabled={disabled}
              >{BUTTON_REMOVE}</ModalCloseButton>
            )}
            <Button
              disabled={loading}
              onClick={onBackPressed}
              appearance="secondary"
            >{BUTTON_BACK}</Button>
            <Button
              disabled={loading}
              onClick={onNextPressed}
              appearance="primary"
            >{BUTTON_NEXT}</Button>
          </ModalFooter>
        </div>
      </ModalBody>
    </div>
  )
}

ClientCredentials.defaultProps = {
  onBackPressed: noop,
}
export default ClientCredentials
